import { parseDayGreeting } from './dayGreeting.js';
/** The re-check interval while the chat is open. */
export const LOOK_RECHECK_MS = 5 * 60_000;
/** While the chat is closed and the launcher shows Gyro: a look changes rarely, and every open tab checks. */
export const LOOK_CLOSED_RECHECK_MS = 15 * 60_000;
/** The desktop reads the gallery under device custody, which costs gyro-svc a Graph call per read. */
export const DESKTOP_LOOK_RECHECK_MS = 15 * 60_000;
/** Coming back on screen re-checks only when the last check is at least this old. */
export const LOOK_REFOCUS_MIN_MS = 60_000;
/** The first retry after a failed check; doubled per failure, up to the normal interval. */
export const LOOK_RETRY_MS = 30_000;
/** A remembered "the look is off" is trusted this long before a closed page asks /v1/status again. */
export const LOOK_OFF_RECHECK_MS = 24 * 60 * 60_000;
const OUTFIT_ID = /^o-[a-z0-9]{10}$/;
const ETAG = /^"look-[a-z0-9-]{1,40}"$/;
const PNG_DATA_URI = /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/;
/** gyro-svc caps the large rendition at 64 KB (87,382 base64 characters); a little headroom for the prefix. */
const DATA_URI_MAX = 87_500;
/** The extra-large rendition (the Office pane's welcome picture) is capped at 160 KB (218,454 base64 characters). */
const XLARGE_DATA_URI_MAX = 218_600;
const NAME_MAX = 40;
const CONTROL = /[\u0000-\u001f\u007f]/;
const isDataUri = (value, max = DATA_URI_MAX) => typeof value === 'string' && value.length <= max && PNG_DATA_URI.test(value);
/** A look answer's `day` (a day outfit's greeting and when its day ends), or null: a malformed one is dropped, the look kept. */
function parseLookDay(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const record = value;
    return parseDayGreeting(record.greeting, record.untilMs);
}
/** gyro-svc's GET /v1/mascot/current answer, or null when it is not a well-formed one. */
export function parseGyroLookAnswer(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const record = value;
    if (typeof record.etag !== 'string' || !ETAG.test(record.etag))
        return null;
    if (record.unchanged === true)
        return { unchanged: true, etag: record.etag };
    const name = typeof record.name === 'string' && record.name.trim() && record.name.length <= NAME_MAX && !CONTROL.test(record.name) ? record.name : null;
    if (!name)
        return null;
    const day = parseLookDay(record.day);
    const dayPart = day ? { day } : {};
    if (record.look === 'default')
        return { look: 'default', name, etag: record.etag, ...dayPart };
    if (typeof record.look !== 'string' || !OUTFIT_ID.test(record.look) || !isDataUri(record.small) || !isDataUri(record.large))
        return null;
    // The extra-large rendition only when it was asked for and is well-formed; a malformed one is dropped, the look kept.
    const xlargePart = isDataUri(record.xlarge, XLARGE_DATA_URI_MAX) ? { xlarge: record.xlarge } : {};
    return { look: record.look, name, etag: record.etag, small: record.small, large: record.large, ...xlargePart, ...dayPart };
}
/** The validator gyro-svc puts on the desktop's gallery view while the look is on (`etag`), when well-formed. */
export function looksViewEtag(view) {
    return typeof view.etag === 'string' && ETAG.test(view.etag) ? view.etag : undefined;
}
/**
 * The desktop's look, from the gallery it already reads under custody: the outfit `showing` names, with its gallery
 * thumbnail (128 px) as both renditions, or the built-in Gyro. Its ETag is the view's own validator when gyro-svc sent
 * one, so the next check can ask only whether the view changed (review L1). On a day outfit's day the view's `day` gives
 * the greeting, which lasts until that day's run ends (`endMs`).
 */
export function lookFromLooksView(view, fetchedThumbnailPng) {
    const viewEtag = looksViewEtag(view);
    const dayPart = view.day ? { day: { greeting: view.day.greeting, untilMs: view.day.endMs } } : {};
    const outfit = view.showing === 'default' ? undefined : view.outfits.find((candidate) => candidate.id === view.showing);
    if (!outfit)
        return { look: 'default', name: 'Gyro', etag: viewEtag ?? '"look-default"', ...dayPart };
    // The thumbnail is inline from an older gyro-svc, or read by the caller when the view came by URL (desktopLookCheck).
    // Neither: the outfit is named and no picture, which outfitToShow reads as the built-in Gyro.
    const png = outfit.thumbnailPng ?? fetchedThumbnailPng;
    const thumbnail = png ? `data:image/png;base64,${png}` : undefined;
    return { look: outfit.id, name: outfit.name, etag: viewEtag ?? `"look-${outfit.id}-${outfit.sha256.slice(0, 16)}"`, ...(thumbnail ? { small: thumbnail, large: thumbnail } : {}), ...dayPart };
}
/**
 * The desktop's look check: the gallery view asked by URL (no thumbnails inline: a 2,000-outfit gallery would be
 * megabytes per check), then ONE thumbnail read, the outfit showing, only when the view carries its URL and not its
 * bytes (an older gyro-svc answers inline, and nothing more is read) and only when the look changed since `since`. A
 * thumbnail read that fails fails the check, so the tracker keeps what is shown and retries on its backoff, instead of
 * showing the built-in Gyro for an outfit the person picked.
 */
export async function desktopLookCheck(client, since) {
    const answer = await client.checkMascotLooks(since);
    if ('unchanged' in answer)
        return answer;
    const bare = lookFromLooksView(answer);
    if (since === bare.etag)
        return { unchanged: true, etag: bare.etag };
    const showing = answer.showing === 'default' ? undefined : answer.outfits.find((candidate) => candidate.id === answer.showing);
    if (!showing || showing.thumbnailPng || !showing.thumbnailUrl)
        return bare;
    return lookFromLooksView(answer, await client.getMascotThumbnail(showing.id));
}
/** The outfit to show, or null for the built-in Gyro (no look yet, the built-in look, or its pictures failed to load). */
export function outfitToShow(look, failedEtag) {
    if (!look || look.look === 'default' || !look.small || !look.large || look.etag === failedEtag)
        return null;
    return look;
}
/** This browser's localStorage, or null where there is none or it refuses access (privacy modes, sandboxes). */
export function browserLookStorage() {
    try {
        return typeof localStorage === 'undefined' ? null : localStorage;
    }
    catch {
        return null;
    }
}
/** The remembered look state, or null when there is none or it is not well-formed. Never throws. */
export function readLookMemory(storage, key) {
    if (!storage || !key)
        return null;
    try {
        const raw = storage.getItem(key);
        if (!raw)
            return null;
        const record = JSON.parse(raw);
        if (record.v !== 1 || typeof record.on !== 'boolean' || typeof record.atMs !== 'number' || !Number.isFinite(record.atMs))
            return null;
        if (!record.on || record.look === undefined)
            return { on: record.on, atMs: record.atMs };
        const look = parseGyroLookAnswer(record.look);
        if (!look || 'unchanged' in look)
            return { on: true, atMs: record.atMs };
        // A day greeting is never remembered across pages: it comes only from a live answer.
        const { day: _day, ...remembered } = look;
        return { on: true, atMs: record.atMs, look: remembered };
    }
    catch {
        return null;
    }
}
/**
 * Remember the look state (null forgets it). Best effort: a full or blocked storage changes nothing shown. The look is
 * kept without its day greeting, which belongs to the day it was read on.
 */
export function writeLookMemory(storage, key, memory) {
    if (!storage || !key)
        return;
    try {
        if (!memory)
            storage.removeItem(key);
        else {
            let look;
            if (memory.on && memory.look) {
                const { day: _day, ...rest } = memory.look;
                look = rest;
            }
            storage.setItem(key, JSON.stringify({ v: 1, on: memory.on, atMs: memory.atMs, ...(look ? { look } : {}) }));
        }
    }
    catch {
        /* storage full or unavailable: the look is still shown for this page */
    }
}
/** Whether a page that has not asked /v1/status yet should, to learn whether the look is on. */
export function lookStatusDue(memory, now) {
    if (!memory)
        return true;
    return !memory.on && now - memory.atMs >= LOOK_OFF_RECHECK_MS;
}
/**
 * When the look is checked. The host says when Gyro is on screen (`setVisible`), how often to check (`setInterval`),
 * when the window regained focus or the page came back from the back/forward cache (`refocus`, treated like coming on
 * screen), and when a pick was just made in this window (`refresh`); one check at a time; a failure keeps the look shown
 * and retries on a doubling backoff (30 s, up to the interval). Nothing runs while off screen, except a check already
 * sent, whose answer is still applied.
 */
export class LookTracker {
    options;
    visible = false;
    disposed = false;
    inFlight = false;
    again = false;
    timer;
    etag;
    failures = 0;
    lastAttemptAt;
    interval;
    refocusMin;
    now;
    setTimer;
    clearTimer;
    constructor(options) {
        this.options = options;
        this.interval = options.intervalMs ?? LOOK_RECHECK_MS;
        this.refocusMin = options.refocusMinMs ?? LOOK_REFOCUS_MIN_MS;
        this.etag = options.initialEtag;
        this.now = options.now ?? (() => Date.now());
        this.setTimer = options.setTimer ?? ((run, ms) => setTimeout(run, ms));
        this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
    }
    setVisible(visible) {
        if (this.disposed || visible === this.visible)
            return;
        this.visible = visible;
        if (!visible) {
            this.cancelTimer();
            return;
        }
        this.scheduleNext(true);
    }
    /**
     * The window regained focus, or the page came back from the back/forward cache: like coming on screen. A tab that was
     * never hidden gets no visibility change, so this is how a pick made in the desktop app reaches a browser tab the
     * person switches back to (2026-10-30). Checks now only when the last check is at least the refocus minimum old (the
     * ETag makes that a 304 in the common case); a pending backoff is kept; nothing while off screen or mid-check.
     */
    refocus() {
        if (this.disposed || !this.visible || this.inFlight)
            return;
        this.scheduleNext(true);
    }
    /** A new interval (the chat opened or closed): the next check moves to the last one plus it. */
    setInterval(intervalMs) {
        if (this.disposed || intervalMs === this.interval)
            return;
        this.interval = intervalMs;
        if (this.visible && !this.inFlight && this.lastAttemptAt !== undefined)
            this.scheduleNext(false);
    }
    /** A pick was just made in this window: check now (after the one in flight, if any). */
    refresh() {
        if (this.disposed)
            return;
        if (this.inFlight) {
            this.again = true;
            return;
        }
        if (this.visible)
            this.schedule(0);
        else
            this.lastAttemptAt = undefined; // checked as soon as Gyro is on screen again
    }
    dispose() {
        this.disposed = true;
        this.visible = false;
        this.cancelTimer();
    }
    get snapshot() {
        return { visible: this.visible, inFlight: this.inFlight, failures: this.failures, etag: this.etag, intervalMs: this.interval };
    }
    /** The doubling retry after `failures` failed checks in a row, never longer than the interval. */
    backoff() {
        return Math.min(this.interval, LOOK_RETRY_MS * 2 ** Math.min(this.failures - 1, 10));
    }
    /**
     * Schedule the next check from the last one: never checked, now; after a failure, its backoff (coming back on screen
     * does not skip it); on coming back on screen, now when the last check is at least the refocus minimum old; else the
     * interval after the last check.
     */
    scheduleNext(comingOnScreen) {
        const last = this.lastAttemptAt;
        if (last === undefined) {
            this.schedule(0);
            return;
        }
        const since = this.now() - last;
        if (this.failures > 0)
            this.schedule(this.backoff() - since);
        else if (comingOnScreen && since >= this.refocusMin)
            this.schedule(0);
        else
            this.schedule(this.interval - since);
    }
    cancelTimer() {
        if (this.timer === undefined)
            return;
        this.clearTimer(this.timer);
        this.timer = undefined;
    }
    schedule(wait) {
        this.cancelTimer();
        if (!this.visible || this.disposed || this.inFlight)
            return;
        this.timer = this.setTimer(() => {
            this.timer = undefined;
            void this.run();
        }, Math.max(0, wait));
    }
    async run() {
        if (!this.visible || this.disposed || this.inFlight)
            return;
        this.inFlight = true;
        this.again = false;
        try {
            const answer = await this.options.fetch(this.etag);
            this.failures = 0;
            this.etag = answer.etag;
            if (!('unchanged' in answer) && !this.disposed)
                this.options.apply(answer);
        }
        catch {
            this.failures += 1;
        }
        finally {
            this.lastAttemptAt = this.now();
            this.inFlight = false;
        }
        if (this.again)
            this.schedule(0);
        else
            this.scheduleNext(false);
    }
}
//# sourceMappingURL=gyroLook.js.map